import type { Series } from './series';

/**
 * Future value of an ordinary annuity (end-of-period contributions).
 * C = periodic contribution, r = annual nominal rate, n = periods/year, t = years.
 * i = r/n, m = n*t
 */
export function annuityFutureValue(C: number, r: number, n: number, t: number): number {
  const i = r / n;
  const m = n * t;
  return i === 0 ? C * m : C * ((Math.pow(1 + i, m) - 1) / i);
}

/**
 * Periodic contribution needed to reach `target` in t years.
 * Inverse of annuityFutureValue.
 */
export function solveContributionForGoal(target: number, r: number, n: number, t: number): number {
  const i = r / n;
  const m = n * t;
  return i === 0 ? target / m : (target * i) / (Math.pow(1 + i, m) - 1);
}

/**
 * Rule of 72: approximate years to double at annual rate r.
 */
export function rule72(r: number): number {
  return 72 / (r * 100);
}

export interface FutureValueSeriesOpts {
  principal?: number;
  startYear?: number;
  stopYear?: number;
}

/**
 * Build a period-by-period Series tracking a growing balance.
 * A lump-sum `principal` compounds throughout.
 * Contributions C (n×/year) are added only while startYear ≤ year < stopYear.
 * Returns points at integer-year boundaries (t=0..years), id = 'fv'.
 */
export function futureValueSeries(
  C: number,
  r: number,
  n: number,
  years: number,
  opts: FutureValueSeriesOpts = {},
): Series {
  const { principal = 0, startYear = 0, stopYear = years } = opts;
  const i = r / n;
  const totalPeriods = years * n;

  let balance = principal;
  const points: Series['points'] = [{ t: 0, value: principal }];

  for (let p = 1; p <= totalPeriods; p++) {
    balance *= 1 + i;
    const year = Math.floor((p - 1) / n);
    if (year >= startYear && year < stopYear) {
      balance += C;
    }
    if (p % n === 0) {
      points.push({ t: p / n, value: balance });
    }
  }

  return { id: 'fv', points };
}

/**
 * Solve for how many years until balance reaches `target`.
 * Returns 0 if already met (target ≤ principal), Infinity if never reached within 100 years.
 */
export function solveYearsForGoal(
  target: number,
  C: number,
  r: number,
  n: number,
  opts: FutureValueSeriesOpts = {},
): number {
  const { principal = 0, startYear = 0, stopYear = Infinity } = opts;

  if (target <= principal) {
    return 0;
  }

  const i = r / n;
  const maxPeriods = 100 * n;
  let balance = principal;

  for (let p = 1; p <= maxPeriods; p++) {
    balance *= 1 + i;
    const year = Math.floor((p - 1) / n);
    if (year >= startYear && year < stopYear) {
      balance += C;
    }
    if (balance >= target) {
      return p / n;
    }
  }

  return Infinity;
}
